import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { Button, Tag } from "./atoms";
import { useLifeAreas } from "./config";
import { getHistory } from "./ringsApi";
import Sparkline from "./Sparkline";
import { fs, ink, SOFT, T, withAlpha } from "./tokens";
import { DimensionKey, RingDimension, RingInsight, RingSnapshot } from "./types";

type Props = {
  dim: RingDimension;
  snapshot: RingSnapshot;
  insight?: RingInsight;
  onEdit: () => void;
  onSelectArea: (key: DimensionKey) => void;
};

// Spec organism AstriaAreaDashboard, in compact form: score header, insight,
// score history, check-in note, sub-dimensions, related areas and the action.
const AreaDetail: React.FC<Props> = ({ dim, snapshot, insight, onEdit, onSelectArea }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const area = L.area(dim.key);
  const [history, setHistory] = useState<number[]>([]);
  const score = L.display(dim.score);
  const prev = L.display(dim.previousScore);
  const band = L.bandFor(score);
  const mood = L.mood(dim.mood);

  // Reloads when the area changes or a new check-in lands.
  useEffect(() => {
    let cancelled = false;
    getHistory(dim.key)
      .then((h) => !cancelled && setHistory(h.map((x) => L.display(x.score) ?? 0)))
      .catch(() => !cancelled && setHistory([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dim.key, snapshot.capturedAt]);

  const Trend = dim.trend === "up" ? ArrowUp : dim.trend === "down" ? ArrowDown : Minus;
  const trendColor =
    dim.trend === "up"
      ? ink(SOFT.up)
      : dim.trend === "down"
        ? ink(SOFT.down)
        : T.color.text.muted;
  const related = area.related;

  return (
    <div
      aria-live="polite"
      style={{
        margin: "8px 0 4px",
        padding: 16,
        background: withAlpha(area.color, 0.06),
        border: `1px solid ${withAlpha(area.color, 0.3)}`,
        borderRadius: T.radius.xl,
        color: T.color.text.primary,
      }}
    >
      <p style={{ ...fs("sm"), color: T.color.text.secondary, margin: 0 }}>{area.description}</p>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
        <span style={{ ...fs("xl", 700), color: band ? ink(band.color) : T.color.text.secondary }}>
          {band ? band.label : t("lifeAreas.detail.notRated")}
        </span>
        {score != null && (
          <span style={{ ...fs("sm"), color: T.color.text.secondary }}>
            {t("lifeAreas.checkin.ofMax", { score, max: L.config.scale.max })}
          </span>
        )}
        {score != null && (
          <span style={{ ...fs("xs"), display: "inline-flex", alignItems: "center", gap: 4, color: trendColor }}>
            <Trend size={14} aria-hidden="true" />
            {prev != null ? t("lifeAreas.detail.from", { score: prev }) : t("lifeAreas.detail.firstRating")}
          </span>
        )}
      </div>

      {score != null && (
        <div style={{ marginTop: 10 }}>
          <Sparkline values={history} color={area.color} max={L.config.scale.max} />
        </div>
      )}

      {insight && (
        <p style={{ ...fs("sm", 500), margin: "12px 0 0" }}>💡 {L.insightText(insight).headline}</p>
      )}
      {(mood || dim.reflection) && (
        <div style={{ marginTop: 10 }}>
          {mood && (
            <p style={{ ...fs("xs"), color: T.color.text.secondary, margin: 0 }}>
              {t("lifeAreas.detail.feeling", { mood: `${mood.emoji} ${mood.label}` })}
            </p>
          )}
          {dim.reflection && (
            <p style={{ ...fs("sm"), fontStyle: "italic", color: T.color.text.secondary, margin: "4px 0 0" }}>
              “{dim.reflection}”
            </p>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
        {area.subDimensions.map((s) => (
          <Tag key={s}>{s}</Tag>
        ))}
      </div>

      {related.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={{ ...fs("xs", 600), color: T.color.text.secondary, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {t("lifeAreas.detail.related")}
          </p>
          <ul style={{ margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {related.map(({ key, note }) => {
              const rel = L.area(key);
              const relDim = snapshot.dimensions.find((d) => d.key === key);
              const relScore = L.display(relDim?.score ?? null);
              return (
                <li key={key} style={{ listStyle: "none", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={!relDim?.isActive}
                    onClick={() => onSelectArea(key)}
                    style={{
                      ...fs("xs", 600),
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: T.color.text.primary,
                      background: withAlpha(rel.color, 0.14),
                      border: `1px solid ${withAlpha(rel.color, 0.5)}`,
                      borderRadius: T.radius.full,
                      padding: "3px 10px",
                      cursor: relDim?.isActive ? "pointer" : "default",
                      opacity: relDim?.isActive ? 1 : 0.5,
                    }}
                  >
                    <span aria-hidden="true">{rel.emoji}</span>
                    {rel.label}
                    <span style={{ color: T.color.text.secondary }}>{relScore ?? "–"}</span>
                  </button>
                  <span style={{ ...fs("xs"), color: T.color.text.secondary }}>{note}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Button variant="area" areaColor={area.color} block onClick={onEdit}>
          {t(score == null ? "lifeAreas.detail.rate" : "lifeAreas.detail.update", { area: area.label })}
        </Button>
      </div>
    </div>
  );
};

export default AreaDetail;
